import { useEffect, useState, useCallback } from 'react'
import { Diamond, FavoritesReturn } from 'types/interfaces'
import StoreFunctions from 'stores/storeFunctions';
import { useAppContext } from 'lib/context/AppContext';
import { Roles } from 'constants/roles';

const STORAGE_KEY_BY_PRODUCT_TYPE = {
  diamond: 'favorites_diamonds',
  gemstone: 'favorites_gemstones'
} as const;

export const useWishlist = (
  productType: keyof typeof STORAGE_KEY_BY_PRODUCT_TYPE = 'diamond'
): FavoritesReturn => {
  const { diamondStore, currencyInfo } = useAppContext()
  const storeFunctions = new StoreFunctions()
  const storageKey = STORAGE_KEY_BY_PRODUCT_TYPE[productType]
  const [products, setProducts] = useState<Diamond[]>([])

  useEffect(() => {
    const favs = localStorage.getItem(storageKey)
    setProducts(favs ? JSON.parse(favs) : [])
    const handleStorage = (e: StorageEvent): void => {
      if (e.key === storageKey) {
        const favs = localStorage.getItem(storageKey)
        setProducts(favs ? JSON.parse(favs) : [])
      }
    }
    window.addEventListener('storage', handleStorage)
    return (): void => {
      window.removeEventListener('storage', handleStorage)
    }
  }, [productType])

  const add = (product: Diamond): void => {
    const favsStorage = localStorage.getItem(storageKey)
    const favs: Diamond[] = favsStorage ? JSON.parse(favsStorage) : []
    const updatedFavorites = [...favs, product]
    setProducts(updatedFavorites)
    localStorage.setItem(storageKey, JSON.stringify(updatedFavorites))
  }

  const remove = (product: Diamond): void => {
    setProducts(prevFavorites => {
      const updatedFavorites = prevFavorites.filter(favorite => favorite.sku !== product.sku)
      localStorage.setItem(storageKey, JSON.stringify(updatedFavorites))
      return updatedFavorites
    })
  }

  const refresh = useCallback(
    async (role: string | undefined, isMemoMode = false) => {
      if (role == undefined) {
        role = storeFunctions.getUserRole()
      }
      const favsStorage = localStorage.getItem(storageKey)
      const favs: Diamond[] = favsStorage ? JSON.parse(favsStorage) : []
      const skus = favs?.map(item => item.sku)
      if (skus.length > 0) {
        const body: { [key: string]: any } = {
          filters: { sku: skus }
        }
        if (role !== Roles.Merchant && !isMemoMode) {
          body.currency_code = currencyInfo.currency
        } else {
          body.currency_code = undefined
        }
        const diamonds = await diamondStore.getDiamond(body, productType, role)
        setProducts(diamonds?.gems || [])
        localStorage.setItem(storageKey, JSON.stringify(diamonds?.gems || []))
      }
    },
    [setProducts, currencyInfo.currency]
  )

  const clear = (): void => {
    localStorage.setItem(storageKey, JSON.stringify([]))
    setProducts([])
  }

  return { products, add, remove, refresh, clear }
}
